/***************************************************
 * Created by gongyanyu on 2022/10/18 17:16. *
 ***************************************************/
import { ISelectableTrait } from '../interfaces/ISelectableTrait';
import { isViewModel } from '../helpers/isViewModel';
import { ListLikeVM } from '../vms/ListLikeVM';
import { action, makeObservable, observable, override } from 'mobx';
import { IModelConstructor } from '../interfaces/IModelConstructor';
import { RecordValue, SimpleRecord } from '../interfaces/types';
import { makeMetadata, property, transformer } from '../helpers/decorators';

export function withSelectable<T extends ListLikeVM = ListLikeVM>(Base: IModelConstructor<T>): IModelConstructor<ISelectableTrait<T> & T> {
  const BaseClass = Base as unknown as typeof ListLikeVM;

  if (!isViewModel(BaseClass)) {
    throw new Error('[VM]: This mixin can only decorate view model');
  }

  class WithSelectable extends BaseClass implements ISelectableTrait<T> {
    constructor(...args: any[]) {
      super(...args);
      makeObservable(this);
      makeMetadata(this);
    }

    private get _isMultipleMode_() {
      return typeof this.multiple === 'boolean' ? this.multiple : typeof this.mode === 'string' ? this.mode === 'multiple' || this.mode === 'tags' : false;
    }

    @observable
    mode: string = undefined;

    @property
    @observable
    multiple: boolean = undefined;

    get _selected_(): RecordValue[] {
      if (this.value === undefined) return [];
      if (!this.labelInValue) return [].concat(this.value);
      return [].concat(this.value).map((d) => d[this.$identifierField]);
    }

    @property
    @observable
    labelInValue: boolean = true;

    @action
    hasValue(value: SimpleRecord | RecordValue): boolean {
      const _id = typeof value === 'object' ? value[this.$identifierField] : value;
      return this._selected_.indexOf(_id) > -1;
    }

    @action
    insertValue(node: SimpleRecord | SimpleRecord[] | RecordValue | RecordValue[]): this {
      if (!this._isMultipleMode_) {
        if (!this.value) {
          this.value = ([] as Array<SimpleRecord | RecordValue>).concat(node).map((node) => this.getNode(node))[0];
        }
        return this;
      }
      const _value = ([] as Array<SimpleRecord | RecordValue>).concat(node).map((node) => this.getNode(node));
      const pure = _value.filter((_node) => !this.hasValue(_node));
      this.value = ([] as Array<SimpleRecord | RecordValue>).concat(this.value, pure);
      return this;
    }

    @action
    removeValue(node: SimpleRecord | SimpleRecord[] | RecordValue | RecordValue[]): this {
      const ids = ([] as Array<SimpleRecord | RecordValue>).concat(node).map((node) => this.getNode(node)[this.$identifierField]);
      if (!this._isMultipleMode_ && !Array.isArray(this.value)) {
        const id = this.getNode(this.value)[this.$identifierField];
        if (ids.indexOf(id) > -1) {
          this.value = undefined;
          return this;
        }
      }
      this.value = (this.value as Array<SimpleRecord | RecordValue>).filter((item: any) => ids.indexOf(this.getNode(item)[this.$identifierField]) === -1);
      return this;
    }

    @override
    setValue(value: SimpleRecord | SimpleRecord[] | RecordValue | RecordValue[]): this {
      if (this._isMultipleMode_ && !Array.isArray(value) && typeof value !== 'undefined') {
        console.error(`[VM]: Type of the value for multi-selector must be Array:`, this);
        throw new Error(`[VM]: Type of the value for multi-selector must be Array`);
      }
      if (!this._isMultipleMode_ && typeof value !== 'object' && typeof value !== 'undefined' && Array.isArray(value)) {
        console.error(`[VM]: Type of the value for single-selector must be a object:`, this);
        throw new Error(`[VM]: Type of the value for single-selector must be a object`);
      }

      this.value = value;
      return this;
    }

    @transformer
    private $valueSetterTransformer(value: Array<SimpleRecord | RecordValue>) {
      if (!this.data || !this.data.length) {
        // 当前组件`VM`没有配置 `data`值
        // 此时将当做 `InputVM` 处理
        // 此为一个回退机制, 很可能使用方配置了错误的`vmType`
        console.error(
          '当前组件`VM`没有配置 `data`值, 此时将回退到 InputVM 处理模式, 如果确定当前组件并不需要VM为其管理`data`, 请使用InputVM.',
          this.$meta.definition,
        );

        return value;
      }
      const _nodes = [].concat(value).map((x) => this.getNode(x));
      return this._isMultipleMode_
        ? this.labelInValue
          ? _nodes
          : _nodes.map((v) => v[this.$identifierField])
        : this.labelInValue
        ? _nodes[0]
        : _nodes[0]?.[this.$identifierField];
    }
  }

  return WithSelectable as unknown as IModelConstructor<ISelectableTrait<T> & T>;
}
